/**
 * Sprint 21 B6: Audit Analytics — Admin Page
 *
 * Track: which competitors audited most, conversion rate from audit → signup,
 * popular comparison points. Real apiFetch. CSS vars only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { BarChart3, TrendingUp, Users, ArrowRight, Loader2 } from "lucide-react";

interface CompetitorAuditStat {
  name: string;
  auditCount: number;
  signupCount: number;
  activeCount: number;
  conversionRate: number;
}

interface AuditAnalytics {
  totalAudits: number;
  totalSignups: number;
  totalActive: number;
  overallConversion: number;
  topCompetitors: CompetitorAuditStat[];
  recentTrend: Array<{ date: string; audits: number; signups: number }>;
}

export default function AuditAnalyticsPage() {
  const [data, setData] = useState<AuditAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: AuditAnalytics }>("/admin/audit-analytics");
      setData(res.data);
    } catch { setData(null); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-[var(--color-surface)]" />)}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
        <BarChart3 className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
        <p className="text-sm text-[var(--color-muted-foreground)]">No audit analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BarChart3 className="h-5 w-5 text-[var(--color-primary)]" />
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Audit Analytics</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">Competitive audit tool performance and conversions</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Total Audits", value: data.totalAudits, icon: BarChart3 },
          { label: "Signups from Audit", value: data.totalSignups, icon: Users },
          { label: "Active Users", value: data.totalActive, icon: TrendingUp },
          { label: "Conversion Rate", value: `${data.overallConversion.toFixed(1)}%`, icon: ArrowRight },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[var(--color-muted-foreground)]">{kpi.label}</span>
              <kpi.icon className="h-4 w-4 text-[var(--color-muted-foreground)]" />
            </div>
            <div className="text-2xl font-bold text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
              {typeof kpi.value === "number" ? kpi.value.toLocaleString() : kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* Conversion funnel */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <h2 className="text-sm font-semibold text-[var(--color-foreground)] mb-4">Conversion Funnel</h2>
        <div className="flex items-center gap-2">
          {[
            { label: "Audit Completed", value: data.totalAudits, pct: 100 },
            { label: "Signed Up", value: data.totalSignups, pct: data.totalAudits > 0 ? (data.totalSignups / data.totalAudits) * 100 : 0 },
            { label: "Active User", value: data.totalActive, pct: data.totalAudits > 0 ? (data.totalActive / data.totalAudits) * 100 : 0 },
          ].map((step, i) => (
            <div key={step.label} className="flex-1 flex items-center gap-2">
              <div className="flex-1">
                <div className="text-xs text-[var(--color-muted-foreground)] mb-1">{step.label}</div>
                <div className="h-8 rounded-lg bg-[var(--color-primary)] flex items-center justify-center" style={{ width: `${Math.max(step.pct, 10)}%` }}>
                  <span className="text-[10px] font-medium text-white" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {step.value.toLocaleString()} ({step.pct.toFixed(1)}%)
                  </span>
                </div>
              </div>
              {i < 2 && <ArrowRight className="h-4 w-4 text-[var(--color-muted-foreground)] shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* Top competitors */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] overflow-hidden">
        <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-3">
          <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Top Competitors Audited</h2>
        </div>
        {data.topCompetitors.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--color-muted-foreground)]">No audits yet</div>
        ) : (
          <div className="divide-y divide-[var(--color-border)]">
            {data.topCompetitors.map((comp, i) => (
              <div key={comp.name} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-4">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-surface)] text-xs font-medium text-[var(--color-muted-foreground)]">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-[var(--color-foreground)]">{comp.name}</p>
                    <p className="text-[10px] text-[var(--color-muted-foreground)]">
                      {comp.signupCount} signups | {comp.activeCount} active
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm font-medium text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {comp.auditCount.toLocaleString()}
                    </p>
                    <p className="text-[10px] text-[var(--color-muted-foreground)]">audits</p>
                  </div>
                  <div className="w-24">
                    <div className="flex items-center justify-between text-[10px] text-[var(--color-muted-foreground)] mb-1">
                      <span>Conv.</span>
                      <span className={comp.conversionRate >= 5 ? "text-[var(--color-success)]" : ""}>
                        {comp.conversionRate.toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[var(--color-surface)]">
                      <div
                        className={`h-1.5 rounded-full ${comp.conversionRate >= 5 ? "bg-[var(--color-success)]" : "bg-[var(--color-primary)]"}`}
                        style={{ width: `${Math.min(comp.conversionRate * 5, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent trend */}
      {data.recentTrend.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <h2 className="text-sm font-semibold text-[var(--color-foreground)] mb-4">Daily Trend (Last 14 Days)</h2>
          <div className="flex items-end gap-1 h-32">
            {data.recentTrend.map((day) => {
              const maxAudits = Math.max(...data.recentTrend.map((d) => d.audits), 1);
              const height = (day.audits / maxAudits) * 100;
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col items-center justify-end" style={{ height: "100px" }}>
                    <div
                      className="w-full rounded-t bg-[var(--color-primary)] min-h-[2px]"
                      style={{ height: `${height}%` }}
                      title={`${day.date}: ${day.audits} audits, ${day.signups} signups`}
                    />
                  </div>
                  <span className="text-[8px] text-[var(--color-muted-foreground)] rotate-[-45deg] origin-top-left whitespace-nowrap">
                    {new Date(day.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
